import { supabase } from './supabaseClient';

/**
 * Service for managing Facebook page selection in Supabase
 */
class FacebookPageSettingsService {
  /**
   * Load selected Facebook page for the current user
   * @returns {Promise<Object|null>} Page object or null if not found
   */
  async loadSelectedPage() {
    try {
      const session = supabase.getSession();
      const user = supabase.getUser();
      const userId = session?.user?.id || session?.user?.sub || user?.id || user?.sub;
      console.info('[FBPageSettings] loadSelectedPage userId:', userId);
      if (!userId) {
        console.error('No authenticated user found');
        return null;
      }
      const { data, error } = await supabase
        .from('facebook_page_settings')
        .select('*')
        .eq('user_id', userId);
      if (error) {
        console.error('Error loading Facebook page settings:', error);
        return null;
      }
      if (!data || data.length === 0) {
        console.info('[FBPageSettings] No page found for user');
        return null;
      }
      let page = data[0].page;
      if (typeof page === 'string') {
        try {
          page = JSON.parse(page);
        } catch (e) {
          console.error('[FBPageSettings] Failed to parse page JSON:', page, e);
          return null;
        }
      }
      console.info('[FBPageSettings] Loaded page:', page);
      return page || null;
    } catch (err) {
      console.error('Error loading Facebook page settings:', err);
      return null;
    }
  }

  /**
   * Save or update selected Facebook page for the current user
   * @param {Object} page - Facebook page object
   * @returns {Promise<boolean>} Success status
   */
  async saveSelectedPage(page) {
    try {
      const session = supabase.getSession();
      const user = supabase.getUser();
      const userId = session?.user?.id || session?.user?.sub || user?.id || user?.sub;
      console.info('[FBPageSettings] saveSelectedPage userId:', userId, 'page:', page);
      if (!userId) {
        console.error('No authenticated user found');
        return false;
      }
      // Check if settings exist
      const { data: existing } = await supabase
        .from('facebook_page_settings')
        .select('id')
        .eq('user_id', userId);

      // If settings exist, delete them first
      if (existing && existing.length > 0) {
        const { error: deleteError } = await supabase
          .from('facebook_page_settings')
          .delete()
          .eq('user_id', userId);
        if (deleteError) {
          console.error('Error deleting old Facebook page settings:', deleteError);
          return false;
        }
      }

      // Insert new/updated settings
      const { error } = await supabase
        .from('facebook_page_settings')
        .insert({ user_id: userId, page });

      if (error) {
        console.error('Error saving Facebook page settings:', error);
        return false;
      }
      console.info('[FBPageSettings] Saved page for user:', userId);
      return true;
    } catch (err) {
      console.error('Error saving Facebook page settings:', err);
      return false;
    }
  }
}

const facebookPageSettingsService = new FacebookPageSettingsService();

export default facebookPageSettingsService;
